import { type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { BUCKETS, type Bucket, type FinanceOverview } from "../../api/finance";
import { money } from "./money";

/**
 * Otvorena potraživanja razložena po kašnjenju, i ujedno filter po njemu.
 *
 * Grafikon i filter su namerno ista stvar. Razlaganje bez filtera odgovara samo koliko se kasni,
 * a ne i ko kasni; filter u padajućem spisku pored grafikona bio bi drugo mesto za istu misao.
 * Zato je svaka traka dugme: klik sužava spisak ispod, ponovni klik vraća sve.
 *
 * Pragovi 7, 30, 60 i 120 dana su dogovoreni sa korisnikom. „Nedospelo" stoji odvojeno jer nije
 * kašnjenje od nula dana nego dug kome rok još nije stigao, pa se ne sabira sa ostalim trakama.
 */

export type Segments = Pick<
  FinanceOverview["totals"],
  "notDueEur" | "d7Eur" | "d30Eur" | "d60Eur" | "d120Eur" | "d120PlusEur" | "carriedEur"
>;

/** Boja raste sa kašnjenjem: zeleno što nije stiglo, crveno što se vuče preko sto dana. */
export const BOJE: Record<Bucket, string> = {
  NEDOSPELO: "var(--ok)",
  D7: "var(--info)",
  D30: "var(--warn)",
  D60: "color-mix(in srgb, var(--warn) 45%, var(--err))",
  D120: "var(--err)",
  D120P: "color-mix(in srgb, var(--err) 72%, var(--ink))",
  // Prenos NIJE najdublje kašnjenje nego nepoznata starost, pa ne dobija najcrvenije polje: boja
  // koja raste sa kašnjenjem ovde ne znači ništa. Neutralna je, da se ne čita kao „najgore".
  PRENOS: "var(--ink-3)",
};

export function bucketValue(s: Segments, b: Bucket): number {
  switch (b) {
    case "NEDOSPELO": return s.notDueEur;
    case "D7": return s.d7Eur;
    case "D30": return s.d30Eur;
    case "D60": return s.d60Eur;
    case "D120": return s.d120Eur;
    case "D120P": return s.d120PlusEur;
    case "PRENOS": return s.carriedEur;
  }
}

export function bucketLabel(b: Bucket, t: (k: string, d: string) => string): string {
  switch (b) {
    case "NEDOSPELO": return t("finance.aging.notDue", "Nedospelo");
    case "D7": return t("finance.aging.d7", "1–7 dana");
    case "D30": return t("finance.aging.d30", "8–30 dana");
    case "D60": return t("finance.aging.d60", "31–60 dana");
    case "D120": return t("finance.aging.d120", "61–120 dana");
    case "D120P": return t("finance.aging.d120p", "preko 120 dana");
    case "PRENOS": return t("finance.aging.carried", "preneto iz ranijih godina");
  }
}

export function AgingChart({
  totals,
  selected,
  onSelect,
  currency = "EUR",
  naDan,
}: {
  totals: Segments;
  selected?: Bucket;
  onSelect: (b: Bucket | undefined) => void;
  /** Valuta iznosa; evro kad je ekran preko svih valuta, inače izabrana valuta. */
  currency?: string;
  /** The day the bands are counted on, already written out; shown under the title. */
  naDan?: string;
}) {
  const { t } = useTranslation();
  const vrednosti = BUCKETS.map((b) => ({ b, v: Math.max(0, bucketValue(totals, b)) }));
  const zbir = vrednosti.reduce((a, x) => a + x.v, 0);
  // Trake se mere prema najvećoj, ne prema zbiru: inače bi sve sitne grupe bile neraspoznatljivo
  // tanke pored one koja nosi najveći deo duga.
  const max = Math.max(...vrednosti.map((x) => x.v), 0);

  if (zbir <= 0) return null;

  return (
    <section style={okvir}>
      <div style={zaglavlje}>
        <div>
          <h2 style={naslov}>{t("finance.aging.overviewTitle", "Potraživanja po kašnjenju")}</h2>
          {naDan && <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{naDan}</div>}
        </div>
        {selected && (
          <button type="button" className="btn sm" onClick={() => onSelect(undefined)}>
            {t("finance.aging.clear", "Prikaži sve")}
          </button>
        )}
      </div>

      {/* Ceo dug u jednoj traci: odnos grupa se vidi pre nego što se pročita ijedan broj. */}
      <div style={traka} aria-hidden="true">
        {vrednosti.map(({ b, v }) =>
          v <= 0 ? null : (
            <div key={b} style={{ width: `${(v / zbir) * 100}%`, background: BOJE[b] }} />
          ),
        )}
      </div>

      <div style={spisak}>
        {vrednosti.map(({ b, v }) => {
          const izabran = selected === b;
          return (
            <button
              key={b}
              type="button"
              style={red(izabran)}
              aria-pressed={izabran}
              onClick={() => onSelect(izabran ? undefined : b)}
            >
              <span style={{ ...tacka, background: BOJE[b] }} />
              <span style={ime}>{bucketLabel(b, t)}</span>
              <span style={merilo}>
                <span style={{ display: "block", height: "100%", borderRadius: 3,
                               width: max > 0 ? `${(v / max) * 100}%` : 0, background: BOJE[b] }} />
              </span>
              <span style={iznos}>{money(v, currency)}</span>
              <span style={udeo}>{Math.round((v / zbir) * 100)}%</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Sastav duga jednog kupca u jednoj tankoj traci, za red u tabeli.
 *
 * Broj u koloni kaže koliko kupac duguje; ovo kaže koliko je taj dug star, što je na spisku od
 * pedeset redova jedina stvar koja razdvaja uobičajeno kašnjenje od problema.
 */
export function AgingStrip({ row, currency = "EUR" }: { row: Segments; currency?: string }) {
  const { t } = useTranslation();
  const vrednosti = BUCKETS.map((b) => ({ b, v: Math.max(0, bucketValue(row, b)) }));
  const zbir = vrednosti.reduce((a, x) => a + x.v, 0);
  if (zbir <= 0) return null;
  const opis = vrednosti
    .filter((x) => x.v > 0)
    .map((x) => `${bucketLabel(x.b, t)}: ${money(x.v, currency)}`)
    .join(" · ");
  return (
    <span style={{ ...traka, width: 90, height: 6, marginTop: 4 }} title={opis}>
      {vrednosti.map(({ b, v }) =>
        v <= 0 ? null : <span key={b} style={{ width: `${(v / zbir) * 100}%`, background: BOJE[b] }} />,
      )}
    </span>
  );
}

const okvir: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
  padding: 16, display: "flex", flexDirection: "column", gap: 12,
};
const zaglavlje: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
};
const naslov: CSSProperties = { fontSize: 15, margin: 0 };
const traka: CSSProperties = {
  display: "flex", width: "100%", height: 10, borderRadius: 999, overflow: "hidden",
  background: "var(--surface-muted)",
};
const spisak: CSSProperties = { display: "flex", flexDirection: "column", gap: 2 };
const red = (izabran: boolean): CSSProperties => ({
  display: "grid", gridTemplateColumns: "10px minmax(90px, 1fr) minmax(60px, 2fr) auto 42px",
  alignItems: "center", gap: 10, width: "100%", textAlign: "left",
  padding: "6px 8px", borderRadius: 6, cursor: "pointer", font: "inherit", fontSize: 13,
  color: "var(--ink-2)",
  background: izabran ? "var(--surface-muted)" : "transparent",
  border: `1px solid ${izabran ? "var(--border-strong)" : "transparent"}`,
});
const tacka: CSSProperties = { width: 10, height: 10, borderRadius: 3 };
const ime: CSSProperties = { whiteSpace: "nowrap" };
const merilo: CSSProperties = {
  display: "block", height: 6, borderRadius: 3, background: "var(--surface-muted)", minWidth: 40,
};
const iznos: CSSProperties = { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const udeo: CSSProperties = {
  textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--ink-3)", fontSize: 12.5,
};
